import Api from '@api/instance';
import { multiSortParamSerializer } from '@api/utils';
import { TEST_STEP_PAGE_SIZE } from '@constants/filter-constants';
import {
  PREVIEW_BASE,
  SAVE_POINT_BASE,
  TEST_CASE_BASE,
} from '@constants/private-url.constant';
import { Page } from '@models/page';
import { Requirement } from '@models/requirement.model';
import { SuiteTestCaseMappings } from '@models/suite-test-case-mappings.model';
import { TestPlanTag } from '@models/test-plan-tag.model';
import { TestSuiteTag } from '@models/test-suite-tag.model';
import { SavePoint } from '@modules/save-points/types/save-point';
import { TestPlan } from '@modules/test-development/models/test-plan.model';
import { TestSuite } from '@modules/test-development/models/test-suite.model';
import {
  Page as OrganismPage,
  TestCase,
  TestData,
  TestDataSet,
  TestDevice,
  ElementIdentifier as UiIdentifier,
} from '@testsigmainc/models';

import { useQuery } from 'react-query';
import { EntityExternalMapping } from '../models/entity-external-mapping.model';
import { ElementTag } from '@models/element-tag.model';

export const getPreviewNameSpace = (savePointId: string) =>
  `${PREVIEW_BASE}/${savePointId}/preview`;

const elementFilter = ({
  filterId,
  savePointId,
  filterQuery = '',
}: {
  filterId: string;
  savePointId: string;
  filterQuery?: string | Record<string, any>;
}) => {
  const isQueryString = !!(typeof filterQuery === 'string');
  return Api.get(
    `${getPreviewNameSpace(savePointId)}/elements/filter/${filterId}${
      isQueryString && filterQuery ? filterQuery : ''
    }`,
    !isQueryString ? filterQuery : null,
  )
    .then((res) => new Page().deserialize(res, UiIdentifier))
    .catch((e) => Promise.reject(e));
};

const testDataFindAll = (
  query?: string,
  sort?: string,
  size?: number,
  page?: number,
  isListView?: boolean,
  savePointId?: string,
) => {
  return Api.get(
    `${getPreviewNameSpace(savePointId)}/test_data/${isListView ? 'list' : ''}`,
    {
      query,
      size,
      sort,
      page,
    },
  )
    .then((res) => new Page().deserialize(res, TestData))
    .catch((e) => Promise.reject(e));
};

const testDataShow = (testDataId: string, savePointId: string) =>
  Api.get(`${getPreviewNameSpace(savePointId)}/test_data/${testDataId}`)
    .then((res) => new TestData().deserialize(res))
    .catch((e) => Promise.reject(e));

const testDataSetsFindAll = ({
  query,
  sort,
  size,
  page,
  savePointId,
}: {
  query?: string;
  sort?: string;
  size?: number;
  page?: number;
  savePointId?: string;
}) =>
  Api.get(`${getPreviewNameSpace(savePointId)}/test_data_sets`, {
    query,
    sort,
    size,
    page,
  })
    .then((res) => new Page().deserialize(res, TestDataSet))
    .catch((e) => Promise.reject(e));

const findPreview = ({ savePointId }: { savePointId: string }) => {
  return Api.get(`${SAVE_POINT_BASE}/${savePointId}`)
    .then((res: SavePoint) => res)
    .catch((e) => Promise.reject(e));
};

const findTestCaseById = ({
  savePointId,
  testCaseId,
}: {
  savePointId: string;
  testCaseId: string;
}) =>
  Api.get(`${getPreviewNameSpace(savePointId)}${TEST_CASE_BASE}/${testCaseId}`)
    .then((res) => new TestCase().deserialize(res))
    .catch((e) => Promise.reject(e));

const restore = ({ savePointId }: { savePointId: string }) =>
  Api.post(`${SAVE_POINT_BASE}/${savePointId}/restore`, {});

const testStepsFindAll = (
  savePointId,
  query?: string,
  sort?: string,
  size?: number,
  page: number = TEST_STEP_PAGE_SIZE,
  stepGroupStepId?: number,
  lastStepId?: number,
  lastStepNumber?: string,
  lastStepType?: TestStepType,
  lastStepConditionType?: TestStepConditionType,
  lastStepParentStepNumber?: string,
  lastStepParentStepId?: number,
  lastStepParentType?: TestStepType,
  lastStepGrandParentStepId?: number,
  lastStepGrandParentsLastStepNumber?: string,
) => {
  return Api.get(`${getPreviewNameSpace(savePointId)}/test_steps`, {
    query,
    sort,
    size,
    page,
    stepGroupStepId,
    lastStepId,
    lastStepNumber,
    lastStepType,
    lastStepConditionType,
    lastStepParentStepNumber,
    lastStepParentStepId,
    lastStepParentType,
    lastStepGrandParentStepId,
    lastStepGrandParentsLastStepNumber,
  });
};

export const useFindPreviewById = ({
  savePointId,
  enabled,
  queryKey,
  staleTime,
}: {
  savePointId: string;
  enabled: boolean;
  queryKey: string;
  staleTime?: number;
}) => {
  return useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey,
    queryFn: () => findPreview({ savePointId }),
    enabled,
    staleTime: staleTime || Infinity,
  });
};

export const showElement = (savePointId: string, elementId: number) => {
  return Api.get(`${getPreviewNameSpace(savePointId)}/elements/${elementId}`)
    .then((res) => res?.map((tag) => new ElementTag().deserialize(tag)))
    .catch((e) => Promise.reject(e));
};
export const showElementTag = (savePointId: string, elementId: number) => {
  return Api.get(
    `${getPreviewNameSpace(
      savePointId,
    )}/element_tags/associate_item/${elementId}`,
  )
    .then((res) => res?.map((tag) => new ElementTag().deserialize(tag)))
    .catch((e) => Promise.reject(e));
};

const showTestCaseById = (savePointId: string, id: number) => {
  return Api.get(`${getPreviewNameSpace(savePointId)}/test_cases/${id}`)
    .then((res) => new TestCase().deserialize(res))
    .catch((e) => {
      throw e;
    });
};

const findAllTestCase = (query?, size?, page?, sort?, savePointId?: string) => {
  return Api.get(`${getPreviewNameSpace(savePointId)}/test_cases/`, {
    query,
    size,
    page,
    sort,
  })
    .then((res) => new OrganismPage<TestCase>().deserialize(res, TestCase))
    .catch((e) => {
      throw e;
    });
};

const getRequirementById = (savePointId: string, requirementId: number) => {
  return Api.get(
    `${getPreviewNameSpace(savePointId)}/requirements/${requirementId}`,
  )
    .then((res) => new Requirement().deserialize(res))
    .catch((e) => Promise.reject(e));
};

const findAllTestSteps = (
  savePointId: string,
  query?: string,
  sort?: string,
  size?: number,
  page: number = TEST_STEP_PAGE_SIZE,
  stepGroupStepId?: number,
  lastStepId?: number,
  lastStepNumber?: string,
  lastStepType?: TestStepType,
  lastStepConditionType?: TestStepConditionType,
  lastStepParentStepNumber?: string,
  lastStepParentStepId?: number,
  lastStepParentType?: TestStepType,
  lastStepGrandParentStepId?: number,
  lastStepGrandParentsLastStepNumber?: string,
) => {
  return Api.get(`${getPreviewNameSpace(savePointId)}/test_steps`, {
    query,
    sort,
    size,
    page,
    stepGroupStepId,
    lastStepId,
    lastStepNumber,
    lastStepType,
    lastStepConditionType,
    lastStepParentStepNumber,
    lastStepParentStepId,
    lastStepParentType,
    lastStepGrandParentStepId,
    lastStepGrandParentsLastStepNumber,
  })
    .then((res) => new TestCase().deserialize(res))
    .catch((e) => {
      throw e;
    });
};

const findAllEntityExternalMapping = (
  savePointId: string,
  query?,
): Promise<Page<EntityExternalMapping>> => {
  return Api.get(`${getPreviewNameSpace(savePointId)}/external_mappings`, {
    query,
  })
    .then((res) =>
      new Page<EntityExternalMapping>().deserialize(res, EntityExternalMapping),
    )
    .catch((e) => Promise.reject(e));
};

const findAllTestPlans = (
  query?: string,
  sort?: string,
  size?: number,
  page?: number,
  savePointId?: string,
) => {
  return Api.get(`${getPreviewNameSpace(savePointId)}/executions`, {
    query,
    sort,
    size,
    page,
  })
    .then((res) => new Page().deserialize(res, TestPlan))
    .catch((e) => Promise.reject(e));
};

const findAllTestSuites = (
  query?: string,
  page?: number,
  sort?: string,
  size?: number,
  savePointId?: string,
) => {
  return Api.get(`${getPreviewNameSpace(savePointId)}/test_suites`, {
    query,
    sort,
    size,
    page,
  })
    .then((res) => new Page().deserialize(res, TestSuite))
    .catch((e) => Promise.reject(e));
};

const findAllSuiteTestCaseMappings = (
  savePointId: string,
  query?: string,
  size?,
  page?,
  sort?,
) => {
  return Api.get(
    `${getPreviewNameSpace(savePointId)}/suite_test_cases_mappings`,
    { query, size, page, sort },
    null,
    multiSortParamSerializer,
  )
    .then((res) =>
      new Page<SuiteTestCaseMappings>().deserialize(res, SuiteTestCaseMappings),
    )
    .catch((e) => {
      throw e;
    });
};

const findAllExecutionEnvironments = (
  savePointId: string,
  filterQuery?: string,
) => {
  return Api.get(
    `${getPreviewNameSpace(savePointId)}/execution_environments${
      filterQuery || ''
    }`,
  )
    .then((res) =>
      new OrganismPage<TestDevice>().deserialize(res, TestDevice as any),
    )
    .catch((e) => Promise.reject(e));
};

const showTestPlan = (savePointId: string, id: number) => {
  return Api.get(`${getPreviewNameSpace(savePointId)}/executions/${id}`)
    .then((res) => new TestPlan().deserialize(res))
    .catch((e) => Promise.reject(e));
};

const showTestSuite = (savePointId: string, id: number) => {
  return Api.get(`${getPreviewNameSpace(savePointId)}/test_suites/${id}`)
    .then((res) => new TestSuite().deserialize(res))
    .catch((e) => Promise.reject(e));
};

const findTestPlanAssociatedTags = (
  savePointId: string,
  planId: string,
): Promise<TestPlanTag[]> => {
  return Api.get(
    `${getPreviewNameSpace(
      savePointId,
    )}/test_plan_tags/associate_item/${planId}`,
  )
    .then((res) => res?.map((tag) => new TestPlanTag().deserialize(tag)))
    .catch((e) => Promise.reject(e));
};

const findTestSuiteAssociatedTags = (
  savePointId: string,
  testSuiteId: number,
): Promise<TestSuiteTag[]> => {
  return Api.get(
    `${getPreviewNameSpace(
      savePointId,
    )}/testsuite_tags/associate_item/${testSuiteId}`,
  )
    .then((res) =>
      (res as any[])?.map((tag) => new TestSuiteTag().deserialize(tag)),
    )
    .catch((e) => Promise.reject(e));
};

const findAllFeaturesAndScenarios = (savePointId: string, query: string) => {
  return Api.get(`${getPreviewNameSpace(savePointId)}/folders/features`, {
    query,
  })
    .then((res) => res)
    .catch((e) => Promise.reject(e));
};

export const PreviewService = {
  findAllTestCase,
  getRequirementById,
  findAllTestSteps,
  showTestCaseById,
  elementFilter,
  testDataFindAll,
  findPreview,
  findTestCaseById,
  testStepsFindAll,
  testDataShow,
  testDataSetsFindAll,
  restore,
  showElement,
  showElementTag,
  findAllEntityExternalMapping,
  findAllTestPlans,
  findAllTestSuites,
  findAllSuiteTestCaseMappings,
  findAllExecutionEnvironments,
  showTestPlan,
  showTestSuite,
  findTestPlanAssociatedTags,
  findTestSuiteAssociatedTags,
  findAllFeaturesAndScenarios,
};
